import React, { useEffect, useState, useCallback, useMemo } from "react";
import Constants from "../../../Variables/Constants";
import { canvasHeight, canvasWidth } from "../components/Customize.constant";
import { Nav, Tab, Tabs, Dropdown, Card, Button } from "react-bootstrap";
import { toast } from "react-toastify";
import "../css/MainFabric.css";
import { Loading } from "../../../../components/Loader";

// Memoized design image component to prevent unnecessary re-renders
const DesignImage = React.memo(
  ({
    image,
    design,
    fabricGroup,
    mainFabricId,
    onImageClick,
    onRemoveClick,
    isActive,
    isHovered,
    onMouseEnter,
    onMouseLeave,
    previewWidth,
    previewHeight,
  }) => {
    const imageUrl = `${Constants.imgDomain}${image.image}`;

    const handleRemove = (e) => {
      e.stopPropagation(); // Prevent triggering the image click
      onRemoveClick(fabricGroup.design_type, design._id, imageUrl);
    };

    return (
      <div
        className={`${
          isActive ? "explore_our_products_card" : ""
        } mb-0 border border-secondary`}
        style={{
          height: "fit-content",
          width: "fit-content",
          position: "relative",
        }}
        key={image._id}
        onMouseEnter={onMouseEnter}
        onMouseLeave={onMouseLeave}
      >
        <img
          loading="lazy"
          style={{
            width: previewWidth,
            height: previewHeight,
            display: "inline",
            objectFit: "contain",
            borderRadius: "0px",
            cursor: mainFabricId ? "pointer" : "not-allowed",
          }}
          className="img-fluid"
          onClick={() =>
            mainFabricId &&
            onImageClick(imageUrl, design.design_type, design._id, design)
          }
          src={imageUrl}
          alt={design.name || "Design"}
        />

        {isHovered && (
          <div
            style={{
              position: "absolute",
              top: "30px",
              left: "50%",
              transform: "translateX(-50%)",
              backgroundColor: "rgba(0, 0, 0, 0.8)",
              color: "white",
              padding: "5px 10px",
              borderRadius: "4px",
              fontSize: "12px",
              whiteSpace: "nowrap",
              zIndex: 10,
              pointerEvents: "none",
            }}
          >
            {design.name || "Design Name"}
          </div>
        )}

        {isActive && (
          <div
            onClick={handleRemove}
            style={{
              cursor: "pointer",
              borderRadius: "0px",
              width: previewWidth,
              height: previewHeight,
            }}
            className="position-absolute bg-xx active z-3 d-flex justify-content-center align-items-center top-0 right-0"
          >
            <img
              src="https://png.pngtree.com/png-vector/20221215/ourmid/pngtree-green-check-mark-png-image_6525691.png"
              alt="Active"
              loading="lazy"
              style={{ objectFit: "contain", width: "40px", height: "40px" }}
              className="p-1"
            />
          </div>
        )}
      </div>
    );
  },
);

DesignImage.displayName = "DesignImage";

const Design = ({
  setRectangles,
  mainFabricId,
  setArt1Thread,
  setArt2Thread,
  setCurrentArt1Design,
  setCurrentArt2Design,
  currentArt1Design,
  currentArt2Design,
  setArt1DesignId,
  setArt2DesignId,
  collectionList,
  onDesignSelect,
  setBaseImageUrl,
  selectedArt1Option,
  setSelectedArt1Option,
  onCollectionDesignChange,
  getAllDesign,
  loading,
  art2Quantities,
  setArt2Quantities,
  dynamicCanvasWidth = canvasWidth,
  dynamicCanvasHeight = canvasHeight,
}) => {
  const [pinnedDesigns, setPinnedDesigns] = useState(() => {
    const saved = localStorage.getItem("pinnedDesigns");
    return saved ? JSON.parse(saved) : [];
  });
  const [hoveredDesign, setHoveredDesign] = useState(null);
  
  // Update parent state only when quantities actually change
  useEffect(() => {
    const selectedDesigns = [];
    const selectedIds = [];
    const selectedThreads = [];
    
    Object.entries(art2Quantities).forEach(([designId, quantity]) => {
      if (quantity > 0) {
        // Find the design from all available designs
        const art2Data = getAllDesign?.find((item) => item && item.design_type === "art2");
        const design = art2Data?.fabrics?.find((d) => d._id === designId);
        
        if (design) {
          selectedDesigns.push(design);
          selectedIds.push(designId);
          selectedThreads.push(design);
        }
      }
    });
    
    // Only update parent state if there's an actual change
    const currentIds = Array.isArray(currentArt2Design) ? currentArt2Design?.map(d => d?._id)?.filter(Boolean) : [];
    const newIds = selectedIds.sort();
    const oldIds = currentIds.sort();
    
    if (JSON.stringify(newIds) !== JSON.stringify(oldIds)) {
      setCurrentArt2Design(selectedDesigns);
      setArt2DesignId(selectedIds);
      setArt2Thread(selectedThreads);
    }
  }, [art2Quantities, getAllDesign]); // Remove parent state from dependencies
  
  // Initialize quantities from currentArt2Design on component mount only
  useEffect(() => {
    if (currentArt2Design && Array.isArray(currentArt2Design) && Object.keys(art2Quantities).length === 0) {
      const initialQuantities = {};
      currentArt2Design.forEach((design) => {
        if (design?._id) {
          initialQuantities[design._id] = 1; // Default to 1 if design is selected
        }
      });
      setArt2Quantities(initialQuantities);
    }
  }, []); // Run only once on mount
  useEffect(() => {
    localStorage.setItem("pinnedDesigns", JSON.stringify(pinnedDesigns));
  }, [pinnedDesigns]);

  const isPinnedDesign = useCallback(
    (designId) => {
      return pinnedDesigns.some(
        (pinnedDesign) => pinnedDesign._id === designId,
      );
    },
    [pinnedDesigns],
  );

  // Memoized design sorting
  const getSortedDesigns = useCallback(
    (designs) => {
      if (!designs || designs.length === 0) return [];

      const unpinnedDesigns = designs.filter(
        (design) => !isPinnedDesign(design._id),
      );

      // Filter and reverse pinned designs for correct order
      const pinnedInCategory = pinnedDesigns
        .filter((pinnedDesign) =>
          designs.some((design) => design._id === pinnedDesign._id),
        )
        .reverse();

      return [...pinnedInCategory, ...unpinnedDesigns];
    },
    [pinnedDesigns, isPinnedDesign],
  );

  // Memoized handleAddImage function
  const handleAddImage = useCallback(
    (src, type, id, design) => {
      if (!mainFabricId) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error("Please select a main fabric first.");
        return;
      }

      if (type === "art1") {
        const img = new window.Image();
        img.crossOrigin = "Anonymous";
        img.src = src;
        img.onload = () => {
          setRectangles((prevImages) => {
            const updatedImages = [...prevImages];
            const existingArt1Index = updatedImages.findIndex((image) => image.type === "art1");

            const newArt1Rectangle = {
              x: 0,
              y: 0,
              width: dynamicCanvasWidth,
              height: dynamicCanvasHeight,
              name: design?.name,
              id: existingArt1Index !== -1 ? updatedImages[existingArt1Index].id : id,
              src,
              type,
            };

            if (existingArt1Index !== -1) {
              updatedImages[existingArt1Index] = newArt1Rectangle;
            } else {
              updatedImages.push(newArt1Rectangle);
            }

            return updatedImages;
          });

          setCurrentArt1Design(design);
          setArt1Thread(design);
          setArt1DesignId(id);
          setBaseImageUrl(src);
          onDesignSelect(design);
        };
        img.onerror = () => {
          // Fallback: use canvas size if image fails to load
          setRectangles((prevImages) => {
            const updatedImages = [...prevImages];
            const existingArt1Index = updatedImages.findIndex((image) => image.type === "art1");
            const newArt1Rectangle = {
              x: 0, y: 0,
              width: dynamicCanvasWidth,
              height: dynamicCanvasHeight,
              name: design?.name,
              id: existingArt1Index !== -1 ? updatedImages[existingArt1Index].id : id,
              src, type,
            };
            if (existingArt1Index !== -1) updatedImages[existingArt1Index] = newArt1Rectangle;
            else updatedImages.push(newArt1Rectangle);
            return updatedImages;
          });
          setCurrentArt1Design(design);
          setArt1Thread(design);
          setArt1DesignId(id);
          setBaseImageUrl(src);
          onDesignSelect(design);
        };
        return;
      }

      // art2 — handled by handleArt2QuantityChange, nothing to do here
      onDesignSelect(design);
    },
    [
      mainFabricId,
      onDesignSelect,
      setRectangles,
      setCurrentArt1Design,
      setArt1Thread,
      setArt1DesignId,
      setBaseImageUrl,
      dynamicCanvasWidth,
      dynamicCanvasHeight,
    ],
  );

  // Handle ART 2 quantity changes
  const handleArt2QuantityChange = useCallback(
    (designId, design, delta) => {
      if (!mainFabricId) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error("Please select a main fabric first.");
        return;
      }

      setArt2Quantities((prev) => {
        const newQuantities = { ...prev };
        const currentQuantity = newQuantities[designId] || 0;
        const newQuantity = Math.max(0, currentQuantity + delta);

        if (newQuantity === 0) {
          delete newQuantities[designId];
        } else {
          newQuantities[designId] = newQuantity;
        }

        return newQuantities;
      });

      // Update rectangles and designs based on quantity
      setRectangles((prevImages) => {
        const imageUrl = `${Constants.imgDomain}${design.images.find((img) => img.is_main === 1)?.image}`;
        const updatedImages = prevImages.filter(
          (img) => img.type !== "art2" || img.designId !== designId,
        );
        const quantity = (art2Quantities[designId] || 0) + delta;

        if (quantity > 0) {
          // Use default size from design data, or fallback to reasonable default
          const defaultWidth = design.default_width || 150;
          const defaultHeight = design.default_height || 150;

          // If user previously resized an instance, inherit that size for new copies.
          // Use uniform scale to preserve aspect ratio.
          const existing = prevImages.find(
            (img) => img && img.type === "art2" && img.designId === designId,
          );
          const uniformScale =
            existing && existing.width && existing.height
              ? Math.min(existing.width / defaultWidth, existing.height / defaultHeight)
              : 1;
          const scaledWidth = Math.max(50, defaultWidth * uniformScale);
          const scaledHeight = Math.max(50, defaultHeight * uniformScale);

          for (let i = 0; i < quantity; i++) {
            updatedImages.push({
              x: 50 + i * 20, // Slight offset for multiple instances
              y: 50 + i * 20,
              width: scaledWidth,
              height: scaledHeight,
              name: design?.name,
              id: `${designId}-${i}`,
              src: imageUrl,
              type: "art2",
              designId: designId,
            });
          }
        }

        return updatedImages;
      });

      // Update current Art2 designs
      setCurrentArt2Design((prev) => {
        const designs = Array.isArray(prev) ? [...prev] : [];
        const existingIndex = designs.findIndex((d) => d?._id === designId);

        if (delta > 0 && existingIndex === -1) {
          designs.push(design);
        } else if (delta < 0 && (art2Quantities[designId] || 0) + delta === 0) {
          return designs.filter((d) => d?._id !== designId);
        }

        return designs;
      });

      setArt2DesignId((prev) => {
        const ids = Array.isArray(prev) ? [...prev] : [];
        const quantity = (art2Quantities[designId] || 0) + delta;

        if (quantity > 0 && !ids.includes(designId)) {
          ids.push(designId);
        } else if (quantity === 0) {
          return ids.filter((id) => id !== designId);
        }

        return ids;
      });

      setArt2Thread((prev) => {
        const threads = Array.isArray(prev) ? [...prev] : [];
        const quantity = (art2Quantities[designId] || 0) + delta;

        if (quantity > 0 && !threads.some((t) => t?._id === designId)) {
          threads.push(design);
        } else if (quantity === 0) {
          return threads.filter((t) => t?._id !== designId);
        }

        return threads;
      });

      onDesignSelect(design);
    },
    [
      art2Quantities,
      setRectangles,
      setCurrentArt2Design,
      setArt2DesignId,
      setArt2Thread,
      onDesignSelect,
      mainFabricId,
    ],
  );

  const handleArt2SizeChange = useCallback(
    (designId, design, factor) => {
      if (!mainFabricId) {
        toast.clearWaitingQueue();
        toast.dismiss();
        toast.error("Please select a main fabric first.");
        return;
      }

      const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
      const minSize = 50;
      const maxSize = 2000;

      setRectangles((prevImages) =>
        prevImages.map((img) => {
          if (!img || img.type !== "art2" || img.designId !== designId) {
            return img;
          }

          const prevW = Number(img.width || 0);
          const prevH = Number(img.height || 0);
          const nextW = clamp(prevW * factor, minSize, maxSize);
          const nextH = clamp(prevH * factor, minSize, maxSize);

          // Keep the object centered while scaling.
          const dx = (nextW - prevW) / 2;
          const dy = (nextH - prevH) / 2;

          return {
            ...img,
            x: Number(img.x || 0) - dx,
            y: Number(img.y || 0) - dy,
            width: nextW,
            height: nextH,
          };
        }),
      );

      onDesignSelect(design);
    },
    [mainFabricId, onDesignSelect, setRectangles],
  );

  // Memoized remove design function - FIXED to remove specific graphic only
  const handleRemoveDesign = useCallback(
    (designType, designId = null, imageUrl = null) => {
      if (designType === "art1") {
        setArt1Thread([]);
        setCurrentArt1Design(null);
        setArt1DesignId(null);
        setRectangles((prevRects) =>
          prevRects.filter((rect) => rect.type !== "art1"),
        );
      } else if (designType === "art2" && designId && imageUrl) {
        // Only remove the specific Art 2 design
        setCurrentArt2Design((prev) =>
          Array.isArray(prev) ? prev.filter((d) => d?._id !== designId) : [],
        );
        setArt2DesignId((prev) =>
          Array.isArray(prev) ? prev.filter((x) => x !== designId) : [],
        );
        setArt2Thread((prev) =>
          Array.isArray(prev) ? prev.filter((t) => t?._id !== designId) : [],
        );

        // Remove the specific rectangle
        setRectangles((prevRects) =>
          prevRects.filter(
            (rect) => !(rect.type === "art2" && rect.src === imageUrl),
          ),
        );
      }
    },
    [
      setRectangles,
      setArt1Thread,
      setCurrentArt1Design,
      setArt1DesignId,
      setArt2Thread,
      setCurrentArt2Design,
      setArt2DesignId,
    ],
  );

  // Memoized render function for design images
  const renderDesignImages = useCallback(
    (fabricGroup) => {
      const designs =
        fabricGroup.design_type === "art2"
          ? getSortedDesigns(fabricGroup.fabrics)
          : fabricGroup.fabrics;

      // Filter only main images once
      const mainImages = [];
      designs.forEach((design) => {
        design.images.forEach((image) => {
          if (image.is_main === 1) {
            mainImages.push({ design, image });
          }
        });
      });

      // Check if a design is active
      const isDesignActive = (design) => {
        if (fabricGroup.design_type === "art1") {
          return currentArt1Design?._id === design._id;
        } else if (fabricGroup.design_type === "art2") {
          return Array.isArray(currentArt2Design)
            ? currentArt2Design.some((d) => d?._id === design._id)
            : false;
        }
        return false;
      };

      // For ART 2, render cards with quantity controls
      if (fabricGroup.design_type === "art2") {
        return (
          <div
            className="d-flex flex-wrap gap-2 overflow-auto"
            style={{ maxHeight: "710px" }}
          >
            {mainImages.map(({ design, image }) => {
              const quantity = art2Quantities[design._id] || 0;
              const imageUrl = `${Constants.imgDomain}${image.image}`;
              const sizeFactor = 1.1;

              return (
                <Card
                  key={`${design._id}-${image._id}`}
                  className={`shadow-sm border ${
                    quantity > 0
                      ? "border-primary bg-primary-light"
                      : "border-secondary"
                  }`}
                  style={{
                    width: "clamp(100px, 18vw, 140px)",
                    height: "clamp(80px, 15vw, 120px)",
                    cursor: mainFabricId ? "pointer" : "not-allowed",
                    transition: "all 0.2s ease",
                    "--bs-primary-light": "rgba(13,110,253,0.05)",
                    backgroundColor: quantity > 0 ? "#8a8a8a" : "transparent",
                    position: "relative",
                    flex: "0 0 auto",
                  }}
                  onClick={() =>
                    !mainFabricId && (() => {
                      toast.clearWaitingQueue();
                      toast.dismiss();
                      toast.error("Please select a main fabric first.");
                    })()
                  }
                >
                  <Card.Body className="p-0 h-100">
                    {/* Image container with overlay */}
                    <div className="h-100 w-100 position-relative overflow-hidden rounded">
                      <img
                        loading="lazy"
                        src={imageUrl}
                        alt={design.name || "Design"}
                        style={{
                          objectFit: "contain",
                          width: "clamp(100px, 15vw, 150px)",
                          height: "clamp(60px, 8vw, 90px)",
                        }}
                      />

                      {/* Price overlay - top right */}
                      <div
                        className="position-absolute top-0 end-0 bg-dark bg-opacity-75 text-white px-2 py-1 "
                        style={{ fontSize: "1rem" }}
                      >
                        ${design.price || 0}
                      </div>

                      {/* Quantity badge - top left */}
                      {quantity > 0 && (
                        <div
                          className="position-absolute top-0 start-0 bg-primary text-white px-2 py-1 small"
                          style={{ fontSize: "clamp(0.5rem, 1vw, 0.7rem)" }}
                        >
                          {quantity}
                        </div>
                      )}

                      {/* Hover overlay with name and total price */}
                      <div
                        className="position-absolute bottom-0 start-0 end-0 bg-dark bg-opacity-75 text-white p-2"
                        style={{
                          opacity: 0,
                          transition: "opacity 0.2s ease",
                          height: "100%",
                          display: "flex",
                          flexDirection: "column",
                          justifyContent: "center",
                          alignItems: "center",
                        }}
                        onMouseEnter={(e) =>
                          (e.currentTarget.style.opacity = "1")
                        }
                        onMouseLeave={(e) =>
                          (e.currentTarget.style.opacity = "0")
                        }
                      >
                        <div className="text-center">
                          <div
                            style={{ fontSize: "1rem", marginBottom: "0.5rem" }}
                          >
                            {design.name || "Design Name"}
                          </div>
                          {quantity > 0 && (
                            <div
                              style={{
                                fontSize: "clamp(0.8rem, 1.6vw, 0.9rem)",
                                color: "#ffd700",
                              }}
                            >
                              Total: ${(design.price || 0) * quantity}
                            </div>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Quantity controls - always visible at bottom */}
                    <div
                      className="position-absolute bottom-0 start-0 end-0 bg-white bg-opacity-90"
                      style={{
                        borderTop: "1px solid #dee2e6",
                        padding: "6px 6px 5px",
                      }}
                    >
                      <div
                        style={{
                          display: "grid",
                          gridTemplateColumns: "20px 1fr 20px",
                          gridAutoRows: "20px",
                          rowGap: "4px",
                          columnGap: "8px",
                          alignItems: "center",
                          justifyItems: "center",
                        }}
                      >
                        {/* Qty */}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Qty -"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArt2QuantityChange(design._id, design, -1);
                          }}
                          disabled={quantity === 0}
                          style={{
                            width: "20px",
                            height: "20px",
                            padding: "0",
                            lineHeight: "18px",
                            fontSize: "12px",
                            borderRadius: "4px",
                          }}
                        >
                          -
                        </Button>
                        <span
                          className="fw-semibold"
                          style={{
                            width: "100%",
                            textAlign: "center",
                            fontSize: "12px",
                            userSelect: "none",
                          }}
                          title="Quantity"
                        >
                          Qty: {quantity}
                        </span>
                        <Button
                          variant="outline-primary"
                          size="sm"
                          title="Qty +"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArt2QuantityChange(design._id, design, 1);
                          }}
                          style={{
                            width: "20px",
                            height: "20px",
                            padding: "0",
                            lineHeight: "18px",
                            fontSize: "12px",
                            borderRadius: "4px",
                          }}
                        >
                          +
                        </Button>

                        {/* Size */}
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Size -"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArt2SizeChange(design._id, design, 1 / sizeFactor);
                          }}
                          disabled={quantity === 0}
                          style={{
                            width: "20px",
                            height: "20px",
                            padding: "0",
                            lineHeight: "18px",
                            fontSize: "12px",
                            borderRadius: "4px",
                          }}
                        >
                          -
                        </Button>
                        <span
                          className="text-muted"
                          style={{
                            width: "100%",
                            textAlign: "center",
                            fontSize: "12px",
                            userSelect: "none",
                          }}
                          title="Size"
                        >
                          Size
                        </span>
                        <Button
                          variant="outline-secondary"
                          size="sm"
                          title="Size +"
                          onClick={(e) => {
                            e.stopPropagation();
                            handleArt2SizeChange(design._id, design, sizeFactor);
                          }}
                          disabled={quantity === 0}
                          style={{
                            width: "20px",
                            height: "20px",
                            padding: "0",
                            lineHeight: "18px",
                            fontSize: "12px",
                            borderRadius: "4px",
                          }}
                        >
                          +
                        </Button>
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              );
            })}
          </div>
        );
      }

      // For ART 1, keep the original grid layout
      const isRectangularPreview = dynamicCanvasWidth > dynamicCanvasHeight;
      const previewWidth = isRectangularPreview ? "120px" : "80px";
      const previewHeight = isRectangularPreview ? "80px" : "80px";
      return (
        <div
          className="d-flex flex-wrap gap-2 overflow-auto"
          style={{ maxHeight: "780px" }}
          onClick={() =>
            !mainFabricId && (() => {
              toast.clearWaitingQueue();
              toast.dismiss();
              toast.error("Please select a main fabric first.");
            })()
          }
        >
          {mainImages.map(({ design, image }) => (
            <DesignImage
              key={`${design._id}-${image._id}`}
              image={image}
              design={design}
              fabricGroup={fabricGroup}
              mainFabricId={mainFabricId}
              onImageClick={handleAddImage}
              onRemoveClick={handleRemoveDesign}
              isActive={isDesignActive(design)}
              isHovered={hoveredDesign === design._id}
              onMouseEnter={() => setHoveredDesign(design._id)}
              onMouseLeave={() => setHoveredDesign(null)}
              previewWidth={previewWidth}
              previewHeight={previewHeight}
            />
          ))}
        </div>
      );
    },
    [
      getSortedDesigns,
      currentArt1Design,
      currentArt2Design,
      mainFabricId,
      handleAddImage,
      handleRemoveDesign,
      hoveredDesign,
      art2Quantities,
      handleArt2QuantityChange,
    ],
  );

  // Memoized sorted tabs
  const sortedTabs = useMemo(() => {
    if (!getAllDesign || getAllDesign.length === 0) return [];

    const art1Data = getAllDesign?.find(
      (item) => item && item.design_type === "art1",
    );
    const art2Data = getAllDesign?.find(
      (item) => item && item.design_type === "art2",
    );

    const sorted = [];
    if (art1Data) sorted.push(art1Data);
    if (art2Data) sorted.push(art2Data);

    return sorted;
  }, [getAllDesign]);

  // Memoized dropdown items
  const dropdownItems = useMemo(
    () =>
      collectionList.map((category) => (
        <Dropdown.Item
          key={category._id}
          onClick={() =>
            onCollectionDesignChange
              ? onCollectionDesignChange(category)
              : setSelectedArt1Option(category)
          }
        >
          {category.title}
        </Dropdown.Item>
      )),
    [collectionList, onCollectionDesignChange, setSelectedArt1Option],
  );

  // Memoized Art 1 tab content
  const art1TabContent = useMemo(() => {
    const art1Data = sortedTabs?.find(
      (item) => item && item.design_type === "art1",
    );
    if (!art1Data) return null;

    return (
      <Tab
        key="art1-tab"
        eventKey="Art 1"
        title={selectedArt1Option?.title || "All Categories"}
      >
        <div className="mb-3">
          <Dropdown>
            <Dropdown.Toggle variant="secondary" id="art1-dropdown">
              {selectedArt1Option?.title || "All Categories"}
            </Dropdown.Toggle>
            <Dropdown.Menu
              style={{
                position: "relative",
                height: "250px",
                overflowY: "scroll",
              }}
            >
              {dropdownItems}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="mt-3">
          {loading && (
            <Loading
              height="450px"
              style={{ transform: "translateX(200%)" }}
              message="Fetching collections..."
            />
          )}
          {!loading && art1Data.fabrics.length > 0 ? (
            renderDesignImages(art1Data)
          ) : (
            <p>No designs available.</p>
          )}
        </div>
      </Tab>
    );
  }, [
    sortedTabs,
    selectedArt1Option,
    loading,
    dropdownItems,
    renderDesignImages,
  ]);
  // Memoized Art 2 tab content
  const art2TabContent = useMemo(() => {
    const art2Data = sortedTabs?.find(
      (item) => item && item.design_type === "art2",
    );
    if (!art2Data) return null;

    // Calculate total price for ART 2 add-ons
    const totalArt2Price = Object.entries(art2Quantities).reduce(
      (total, [designId, quantity]) => {
        const design = art2Data.fabrics.find((d) => d._id === designId);
        if (design && quantity > 0) {
          return total + (design.price || 0) * quantity;
        }
        return total;
      },
      0,
    );

    return (
      <Tab key="art2-tab" eventKey="Art 2" title="Graphic Add-ons">
        {/* Total price summary for ART 2 */}
        {totalArt2Price > 0 && (
          <div className="bg-light p-3 rounded mb-3">
            <div className="d-flex justify-content-between align-items-center">
              <h3 className="mb-0">Total Graphic Add-ons Price</h3>
              <h4 className="mb-0 text-primary">${totalArt2Price}</h4>
            </div>
          </div>
        )}

        {art2Data.fabrics.length > 0 ? (
          renderDesignImages(art2Data)
        ) : (
          <p>No designs available.</p>
        )}
      </Tab>
    );
  }, [sortedTabs, renderDesignImages, art2Quantities]);
  return (
    <div
      className="row flex-column overflow-hidden  m-1"
      style={{
        backgroundColor: "#E8E9ED",
        borderRadius: "10px",
      }}
    >
      <div className="col-12">
        <div
          className="row flex-column gap-4 py-4"
          style={{ minHeight: "400px" }}
        >
          {getAllDesign.length > 0 ? (
            <Tabs id="fabric-design-tabs" className="mb-3" key="design-data">
              {art1TabContent}
              {art2TabContent}
            </Tabs>
          ) : (
            <p className="text-start fs-14 fw-semibold">
              {loading ? "Loading designs..." : "No designs available"}
            </p>
          )}
        </div>
      </div>
    </div>
  );
};

export default React.memo(Design);
